import { useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import Flag from 'react-flagkit'
import notificationStyles from './NotificationBar.module.css'
import styles from './RegionManager.module.css'


const AVAILABLE_REGIONS = [
  { code: 'de', label: 'Germany', flag: 'DE' },
  { code: 'fr', label: 'France', flag: 'FR' },
  { code: 'it', label: 'Italy', flag: 'IT' },
  { code: 'es', label: 'Spain', flag: 'ES' },
]

export default function RegionManager({ regions, setRegions, availableRegions, selectionMode = 'multiple' }) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const containerRef = useRef(null)
  const selectedRegions = useMemo(() => Array.isArray(regions) ? regions : [], [regions])

  const regionOptions = useMemo(() => {
    if (Array.isArray(availableRegions) && availableRegions.length) {
      const normalized = Array.from(
        new Set(
          availableRegions
            .map(code => String(code || '').toLowerCase())
            .filter(Boolean)
        )
      )

      const filtered = AVAILABLE_REGIONS.filter((region) => normalized.includes(region.code))
      if (filtered.length) {
        return filtered
      }
    }
    return AVAILABLE_REGIONS
  }, [availableRegions])

  const toggleRegion = (regionCode) => {
    if (!regionOptions.some((region) => region.code === regionCode)) {
      return
    }

    if (selectionMode === 'single') {
      if (selectedRegions.length === 1 && selectedRegions[0] === regionCode) {
        setIsDropdownOpen(false)
        return
      }
      setRegions([regionCode])
      setIsDropdownOpen(false)
      return
    }

    if (selectedRegions.includes(regionCode)) {
      if (selectedRegions.length > 1) {
        setRegions(selectedRegions.filter(r => r !== regionCode))
      }
    } else {
      setRegions([...selectedRegions, regionCode])
    }
  }

  useEffect(() => {
    if (!isDropdownOpen) return

    const handleOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [isDropdownOpen])

  return (
    <div className="relative" ref={containerRef}>
      <button
        type='button'
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className={classNames(
          'relative inline-flex items-center gap-2 h-9 px-3 rounded-lg border text-sm font-medium transition-colors focus:outline-none',
          isDropdownOpen
            ? 'border-primary text-primary bg-secondary'
            : 'border-neutral-200 dark:border-neutral-700 text-textTitle bg-background hover:border-primary'
        )}
      >
        <i className="bx bx-flag text-[17px] leading-none" aria-hidden />
        Märkte
        <i className={classNames('bx bx-chevron-down text-base leading-none -ml-0.5', isDropdownOpen ? 'text-primary' : 'text-neutral-400')} aria-hidden />
      </button>
      {isDropdownOpen && (
        <div className={classNames(notificationStyles.tooltip, styles.menu)}>
          <div className={classNames(notificationStyles.dropdownContent, styles.menuContent)}>
            <section>
              <header>Märkte</header>
              <div className={notificationStyles.marketFlags}>
                {regionOptions.map((region) => {
                  const isActive = selectedRegions.includes(region.code)
                  return (
                    <button
                      key={region.code}
                      type='button'
                      onClick={() => toggleRegion(region.code)}
                      className={classNames(notificationStyles.flagButton, {
                        [notificationStyles.flagButtonActive]: isActive,
                      })}
                    >
                      <Flag country={region.flag} size={24} aria-label={region.label} />
                    </button>
                  )
                })}
              </div>
            </section>
          </div>
        </div>
      )}
    </div>
  )
}
